'use client';

import Link from 'next/link';
import { type JSX, useState, useCallback, useEffect, useRef } from 'react';

import { useFormValidation } from '../hooks/useFormValidation';
import { EmailInput } from './EmailInput';
import { PasswordInput } from './PasswordInput';
import { SSOPrompt } from './SSOPrompt';
import { useSSO } from './useSSO';

export interface LoginFormInputsProps {
    ssoError?: string;
}

export function LoginFormInputs({ ssoError: initialSsoError }: LoginFormInputsProps): JSX.Element {
    const [showPassword, setShowPassword] = useState(false);
    const {
        isFormValid,
        errors,
        touched,
        refs: { emailInputRef, passwordInputRef, checkboxRef },
        handleInputChange,
        handleBlur,
        handleFocus,
        validateField,
    } = useFormValidation({ validatePasswordStrength: false });

    const { isSSOEnabled, isCheckingSSO, isRedirectingToSSO, ssoError, checkSSODomain, initiateSSOLogin, disableSSO } =
        useSSO(initialSsoError);

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const handleEmailChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            handleInputChange();

            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                const email = emailInputRef.current?.value || '';
                if (!validateField('email', email) && email) {
                    void checkSSODomain(email);
                } else {
                    disableSSO();
                }
            }, 500);
        },
        [handleInputChange, validateField, checkSSODomain, disableSSO, emailInputRef],
    );

    const handleEmailBlur = useCallback((): void => {
        handleBlur('email');

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }

        const email = emailInputRef.current?.value || '';
        if (!validateField('email', email) && email) {
            void checkSSODomain(email);
        } else {
            disableSSO();
        }
    }, [handleBlur, emailInputRef, validateField, checkSSODomain, disableSSO]);

    const handleSSOContinue = useCallback(async (): Promise<void> => {
        const email = emailInputRef.current?.value || '';
        if (validateField('email', email) || !email) {
            handleBlur('email');
            return;
        }

        // Validate checkbox is checked
        const isCheckboxChecked = checkboxRef.current?.checked ?? false;
        if (!isCheckboxChecked) {
            handleBlur('checkbox');
            return;
        }

        await initiateSSOLogin(email);
    }, [emailInputRef, checkboxRef, initiateSSOLogin, validateField, handleBlur]);

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Re-validate form when SSO state changes (password field mounts/unmounts)
    useEffect(() => {
        handleInputChange();
    }, [isSSOEnabled, handleInputChange]);

    return (
        <>
            <EmailInput
                inputRef={emailInputRef}
                error={errors.email}
                touched={touched.email}
                disabled={isCheckingSSO}
                isCheckingSSO={isCheckingSSO}
                onChange={handleEmailChange}
                onFocus={() => handleFocus('email')}
                onBlur={handleEmailBlur}
            />

            {!isSSOEnabled && (
                <PasswordInput
                    inputRef={passwordInputRef}
                    error={errors.password}
                    touched={touched.password}
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                    onChange={handleInputChange}
                    onFocus={() => handleFocus('password')}
                    onBlur={() => handleBlur('password')}
                />
            )}

            <div className="mb-6">
                <div className="flex items-start">
                    <input
                        ref={checkboxRef}
                        id="agreement-checkbox"
                        name="agreement-checkbox"
                        type="checkbox"
                        required
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-1"
                        onChange={handleInputChange}
                        onFocus={() => handleFocus('checkbox')}
                        onBlur={() => handleBlur('checkbox')}
                    />
                    <label htmlFor="agreement-checkbox" className="ml-2 block text-sm text-gray-700">
                        I have read and accept the{' '}
                        <Link className="text-blue-700 underline" href="/terms-of-service">
                            Terms of Service
                        </Link>
                        ,{' '}
                        <Link className="text-blue-700 underline" href="/privacy">
                            Privacy Policy
                        </Link>
                        , and the License Agreement that applies to my plan:{' '}
                        <Link className="text-blue-700 underline" href="/license/individual-license">
                            Individual License
                        </Link>{' '}
                        /{' '}
                        <Link className="text-blue-700 underline" href="/license/business-license">
                            Business License
                        </Link>
                        .
                    </label>
                </div>
                {touched.checkbox && errors.checkbox && (
                    <div className="mt-1 ml-6 text-sm text-red-600">{errors.checkbox}</div>
                )}
            </div>

            {isSSOEnabled ? (
                <SSOPrompt
                    ssoError={ssoError}
                    isRedirectingToSSO={isRedirectingToSSO}
                    disabled={!isFormValid}
                    onContinue={() => {
                        void handleSSOContinue();
                    }}
                    onFallbackToPassword={disableSSO}
                />
            ) : (
                <>
                    <button
                        type="submit"
                        disabled={!isFormValid}
                        className={`w-full py-3 px-4 rounded font-medium transition-colors ${
                            isFormValid
                                ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        Login
                    </button>

                    <div className="mt-6 text-center space-y-2">
                        <div>
                            <Link href="/password/request-reset" className="text-blue-700 hover:underline text-sm">
                                Forgot Password?
                            </Link>
                        </div>
                        <div>
                            <Link
                                href="https://contribute.storyblocks.com"
                                className="text-blue-700 hover:underline text-sm"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Contributor
                            </Link>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
